import React, { useState, useCallback } from "react";
import bgImage from "../assets/background.png";
import type { VendorFormData, FormErrors, SelectOption } from "../types/vendor";
import AgileAPLogo from "../assets/AgileAp-Logo-Standard.png";
import { submitVendorData } from "../services/vendorService";



// --- Reusable UI Components (defined outside the main component to prevent re-creation) ---

interface InputFieldProps {
	id: keyof VendorFormData;
	label: string;
	value?: string;
	onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
	error?: string;
	type?: "text" | "email" | "tel";
	placeholder?: string;
	as?: "input" | "textarea";
}

const InputField: React.FC<InputFieldProps> = ({ id, label, error, as = "input", value, ...props }) => {
	const commonClasses =
		"w-full px-4 py-2.5 bg-slate-50 border border-slate-300 rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition duration-200";
	const errorClasses = "border-red-500 focus:ring-red-500 focus:border-red-500";
	const InputComponent: any = as;

	return (
		<div className="w-full">
			<label htmlFor={String(id)} className="block text-sm font-medium text-slate-700 mb-1.5">
				{label}
			</label>
			<InputComponent
				id={String(id)}
				name={String(id)}
				value={value || ""}
				className={`${commonClasses} ${error ? errorClasses : ""} placeholder-slate-300`}
				{...props}
			/>
			{error && <p className="mt-1.5 text-xs text-red-600">{error}</p>}
		</div>
	);
};

interface SelectFieldProps {
	id: keyof VendorFormData;
	label: string;
	value?: string;
	onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
	error?: string;
	options: SelectOption[];
}

const SelectField: React.FC<SelectFieldProps> = ({ id, label, value, onChange, error, options }) => {
	const commonClasses =
		"w-full px-4 py-2.5 bg-slate-50 border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition duration-200 appearance-none";
	const errorClasses = "border-red-500 focus:ring-red-500 focus:border-red-500";

	return (
		<div className="w-full relative">
			<label htmlFor={String(id)} className="block text-sm font-medium text-slate-700 mb-1.5">
				{label}
			</label>
			<select
				id={String(id)}
				name={String(id)}
				value={value || ""}
				onChange={onChange}
				className={`${commonClasses} ${error ? errorClasses : ""}`}
			>
				<option value="" disabled>
					Select an option
				</option>
				{options.map((option) => (
					<option key={option.value} value={option.value} className="bg-white">
						{option.label}
					</option>
				))}
			</select>
			<div className="pointer-events-none absolute inset-y-0 right-0 top-7 flex items-center px-2 text-slate-500">
				<svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
					<path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
				</svg>
			</div>
			{error && <p className="mt-1.5 text-xs text-red-600">{error}</p>}
		</div>
	);
};

const annualRevenueOptions: SelectOption[] = [
	{ value: "0-1M", label: "Below $1 Million" },
	{ value: "1M-5M", label: "$1 Million to $5 Million" },
	{ value: "5M-25M", label: "$5 Million to $25 Million" },
	{ value: "25M-100M", label: "$25 Million to $100 Million" },
	{ value: "100M+", label: "Over $100 Million" },
];

const VendorOnboardingSG: React.FC = () => {
	const initialFormData: VendorFormData = {
		txUEN: "",
		txGSTNo: "",
		txCompanyName: "",
		txAddress: "",
		txContactPerson: "",
		txContactNumber: "",
		txEmail: "",
		txConfirmEmail: "",
		txPeppolID: "",
		txType: "Onboarding Form Request (SG)",
		companyId: 0,
		statusId: 7,
		onboardingType: "BTSG",
	};

	const [formData, setFormData] = useState<VendorFormData>(initialFormData);
	const [errors, setErrors] = useState<FormErrors>({});
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [submissionStatus, setSubmissionStatus] = useState<"idle" | "success" | "error">("idle");
	const [submissionErrorMessage, setSubmissionErrorMessage] = useState<string>("");

	const validateForm = useCallback((data: VendorFormData): FormErrors => {
		const newErrors: FormErrors = {};
		if (!data.txUEN) newErrors.txUEN = "UEN is required";
		// else if (!/^\d{9}[A-Za-z]$/.test(data.txUEN)) newErrors.txUEN = "Invalid format (e.g., 123456789X)";

		if (!data.txGSTNo) newErrors.txGSTNo = "GST No. is required";
		// else if (!/^\d{9}[A-Za-z]$/.test(data.txGSTNo) && data.txGSTNo.toUpperCase() !== "NA") newErrors.txGSTNo = "Invalid format (e.g., 123456789X)";

		if (!data.txCompanyName) newErrors.txCompanyName = "Company Name is required";
		if (!data.txAddress) newErrors.txAddress = "Address is required";
		if (!data.txContactPerson) newErrors.txContactPerson = "Contact Person is required";

		if (!data.txContactNumber) newErrors.txContactNumber = "Contact Number is required";
		else if (!/^(\+?65)[0-9]{8}$/.test(data.txContactNumber))
			newErrors.txContactNumber = "Invalid Singapore phone number format";

		if (!data.txEmail) newErrors.txEmail = "Email is required";
		else if (!/\S+@\S+\.\S+/.test(data.txEmail)) newErrors.txEmail = "Invalid email address";

		if (!data.txConfirmEmail) newErrors.txConfirmEmail = "Please confirm your email";
		else if (data.txEmail !== data.txConfirmEmail) newErrors.txConfirmEmail = "Emails do not match";

		if (!data.txPeppolID) newErrors.txPeppolID = 'Peppol ID is required';
		else if (!/^\d{4}:[A-Za-z0-9-]+$/.test(data.txPeppolID) && data.txPeppolID.toUpperCase() !== "NA") newErrors.txPeppolID = "Invalid Peppol ID format (e.g., 0195:SGUEN123456789X)";

		return newErrors;
	}, []);

	const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
		const { name, value } = e.target;
		setFormData((prev) => ({ ...prev, [name]: value }));
		if ((errors as any)[name as keyof FormErrors]) {
			setErrors((prev) => {
				const newErrors = { ...prev };
				delete (newErrors as any)[name as keyof FormErrors];
				return newErrors;
			});
		}
	};

	const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		const validationErrors = validateForm(formData);
		setErrors(validationErrors);

		if (Object.keys(validationErrors).length > 0) {
			return;
		}

		setIsSubmitting(true);
		setSubmissionStatus("idle");
		try {
			await submitVendorData(formData);
			setSubmissionStatus("success");
			setFormData(initialFormData);
		} catch (error) {
			const responseMessage = (typeof error === "string" ? error : (error as any)?.message) || "";
			if (responseMessage.toLowerCase().includes("exist")) {
				setSubmissionStatus("error");
				setSubmissionErrorMessage(responseMessage);
				return;
			}
			console.error("Submission failed:", error);
			setSubmissionStatus("error");
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<div
			className="relative min-h-screen bg-white w-full text-slate-800 flex items-center justify-center p-4 sm:p-6 lg:p-8"
			style={{
				backgroundImage: `url(${bgImage})`,
				backgroundSize: "cover",
				backgroundPosition: "center",
				backgroundRepeat: "no-repeat",
			}}
		>
			<div className="absolute inset-0 bg-black/10 z-0" aria-hidden="true" />
			<main className="relative z-10 w-full max-w-4xl mx-auto bg-white/80 backdrop-blur-md border border-white/20 rounded-2xl shadow-2xl p-6 sm:p-8 lg:p-12">
				<div className="text-center mb-10">
					<div className="inline-block" aria-label="agileap logo">
						<img src={AgileAPLogo} alt="AgileAP Logo" className="h-20 mx-auto mb-2" />
						{/* <span className="text-5xl font-bold text-slate-800 tracking-tight">agile</span> */}
						{/* <span className="text-5xl font-bold text-orange-600">ap</span> */}
					</div>
					<h1 className="text-2xl sm:text-3xl font-bold text-slate-800 mt-4">Onboarding Form</h1>
					<p className="mt-2 text-slate-600">Please fill out the form to begin our partnership.</p>
					<p className="mt-2 text-slate-600">If the field is not applicable to your company, kindly indicate NA.</p>
				</div>

				{submissionStatus === "success" && (
					<div
						className="bg-green-100 border border-green-200 text-green-800 px-4 py-3 rounded-lg relative text-center"
						role="alert"
					>
						<strong className="font-bold">Success!</strong>
						<span className="block sm:inline ml-1">
							{" "}
							Your application has been submitted. We will be in touch shortly.
						</span>
					</div>
				)}

				{submissionStatus === "error" && (
					<div
						className="bg-red-100 border border-red-200 text-red-800 px-4 py-3 rounded-lg relative text-center"
						role="alert"
					>
						<strong className="font-bold">Error!</strong>
						<span className="block sm:inline ml-1">
							{" "}
							{submissionErrorMessage != "" ? submissionErrorMessage : "There was a problem submitting your form. Please try again later."}
						</span>
					</div>
				)}

				<form onSubmit={handleSubmit} noValidate className="mt-8 space-y-6">
					<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
						<InputField
							id="txCompanyName"
							label="Company Name"
							value={formData.txCompanyName}
							onChange={handleChange}
							error={errors.txCompanyName}
							placeholder="Your Company Inc."
						/>
						<InputField
							id="txContactPerson"
							label="Contact Person"
							value={formData.txContactPerson}
							onChange={handleChange}
							error={errors.txContactPerson}
							placeholder="Jane Doe"
						/>
						<InputField
							id="txUEN"
							label="UEN"
							value={formData.txUEN}
							onChange={handleChange}
							error={errors.txUEN}
							placeholder="123456789X"
						/>
						<InputField
							id="txGSTNo"
							label="GST No. (or 'NA', if not applicable)"
							value={formData.txGSTNo}
							onChange={handleChange}
							error={errors.txGSTNo}
							placeholder="123456789X"
						/>
						<InputField
							id="txContactNumber"
							label="Contact Number"
							type="tel"
							value={formData.txContactNumber}
							onChange={handleChange}
							error={errors.txContactNumber}
							placeholder="+65 9123 4567"
						/>
						<InputField
							id="txPeppolID"
							label="Peppol ID (or 'NA', if not applicable)"
							value={formData.txPeppolID}
							onChange={handleChange}
							error={errors.txPeppolID}
							placeholder="0195:SGUEN123456789X"
						/>
						<InputField
							id="txEmail"
							label="Email Address"
							type="email"
							value={formData.txEmail}
							onChange={handleChange}
							error={errors.txEmail}
							placeholder="you@company.com"
						/>
						<InputField
							id="txConfirmEmail"
							label="Confirm Email Address"
							type="email"
							value={formData.txConfirmEmail}
							onChange={handleChange}
							error={errors.txConfirmEmail}
							placeholder="you@company.com"
						/>
						<div className="md:col-span-2">
							<InputField
								as="textarea"
								id="txAddress"
								label="Company Address"
								value={formData.txAddress}
								onChange={handleChange}
								error={errors.txAddress}
								placeholder="1234 Market St, Singapore 567890"
							/>
						</div>
					</div>

					<div className="pt-4">
						<button
							type="submit"
							disabled={isSubmitting}
							className="w-full flex justify-center items-center px-6 py-3.5 border border-transparent text-base font-medium rounded-lg text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-white focus:ring-orange-500 transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
						>
							{isSubmitting ? (
								<>
									<svg
										className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
										xmlns="http://www.w3.org/2000/svg"
										fill="none"
										viewBox="0 0 24 24"
									>
										<circle
											className="opacity-25"
											cx="12"
											cy="12"
											r="10"
											stroke="currentColor"
											strokeWidth="4"
										></circle>
										<path
											className="opacity-75"
											fill="currentColor"
											d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
										></path>
									</svg>
									Submitting...
								</>
							) : (
								"Submit Application"
							)}
						</button>
					</div>
				</form>
			</main>
		</div>
	);
};

export default VendorOnboardingSG;
